import { BREAKPOINTS, LoadingIndicator } from "@laerdal/life-react-components";
import axios from "axios";

import styled from "styled-components";
import { useEffect, useState } from "react";
import ProductApi from "../../services/api/ProductApi";
import { ProductLineDetailsDto } from "../../model/dto/productLineDetailsDto";
import { useParams } from "react-router-dom";
import ProductTopSection from "../../commonComponents/ProductTopSection";
import Faqs from "../../commonComponents/Faqs";
import CantFindWhatYouNeed from "../../commonComponents/CantFindWhatYouNeed";
import Topics from "../../commonComponents/Topics";
import CantFindCreateCase from "../../commonComponents/CantFindCreateCase";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import { useMediaMatch } from "rooks";
import { useTranslation } from "react-i18next";
import posthog from 'posthog-js';
import { useSelector } from "react-redux";
import { buildMarketPath, selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";
import { marketList } from "../../model/constants/marketContants";
import TranslateWithAI from "../../commonComponents/TranslateWithAI";
import ProductResources from "./components/ProductResources";
import ProductAssets from "./components/ProductAssets";
import MostCommonInquiries from "./components/MostCommonInquiries";
import ArticlesSection from "./components/ArticlesSection";

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap:64px;
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    ${BREAKPOINTS.MEDIUM}{
        gap: 64px;
    }
`;

const CantFindWrapper = styled.div`
    padding: 24px 0;
    background: var(--Neutral-020-tint, #FAFAFA);
`;

const FooterContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap:64px;
    
`;

const ProductLinePage3 = () => {
    const { categoryLink, productLink } = useParams<{ categoryLink: string, productLink: string }>();
    const { t } = useTranslation("landingPage");

    const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));
    const isMobile = !isMediumScreen; // Use existing pattern for mobile detection

    const [productLineLoading, setProductLineLoading] = useState<boolean>(true);
    const [productLine, setProductLine] = useState<ProductLineDetailsDto | undefined>(undefined);

    const currentMartket = useSelector(selectMarket);
    const translateWithAi = useSelector(selectTranslateWithAi);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        setProductLineLoading(true);
        setProductLine(undefined);

        if (categoryLink && productLink) {
            ProductApi
                .GetProductLine(productLink, (categoryLink != 'medical-devices' && translateWithAi) ? marketList[currentMartket].locale : 'en', signal)
                .then((data) => {
                    posthog.capture('SUPP-ProductPageView', {
                        page: data.displayName,
                        productLineCode: productLink
                    });

                    setProductLineLoading(false);
                    setProductLine(data);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;
                })
        }

        return () => {
            controller.abort();
        };
    }, [categoryLink, productLink, translateWithAi, currentMartket]);

    return (
        <>
            {productLineLoading &&
                <LoadingIndicator />
            }

            {productLine &&
                <Wrapper>
                    <ProductTopSection
                        title={productLine.displayName}
                        description={productLine.descripton}
                        asset={productLine.mainImageUrl}
                        breadcrumb={[{
                            text: t('Home'),
                            link: `/${currentMartket}`
                        }, {
                            text: t(productLine.categoryName),
                            link: buildMarketPath(currentMartket, `/category/${categoryLink}`)
                        }, {
                            text: productLine.displayName
                        }]}
                        buttonLink={productLine.productDownloadsUrl ?? undefined}
                        productLineCode={productLink}
                    />
                    
                    <ProductResources
                        currentMarket={currentMartket}
                        isMobile={isMobile}
                        categoryLink={categoryLink}
                        productLink={productLink}
                        knowledgeHubLink={productLine.knowledgeHubLink}
                        howToVideosLink={productLine.howToVideosLink}
                        userGuideLink={productLine.userGuideLink}
                        partsAndAccessoriesLink={productLine.partsAndAccessoriesLink}
                        hasDownloads={productLine.hasDownloads}
                        hasOnboarding={productLine.hasOnboarding} />
                    
                    <ProductAssets
                        currentMarket={currentMartket}
                        categoryLink={categoryLink}
                        productLink={productLink}
                        productLineName={productLine.productLineName} />
                    

                    {productLine.components.length > 0 &&
                        <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true} $margin="0 auto">
                            {marketList[currentMartket].locale != 'en' && <TranslateWithAI isMedicalProduct={categoryLink == 'medical-devices'} />}
                            <PageContainer>
                                <MostCommonInquiries topIssues={productLine.topIssues} />
                                <ArticlesSection
                                    categoryLink={categoryLink}
                                    productLink={productLink}
                                    components={productLine.components}
                                    isMediumScreen={isMediumScreen} />
                            </PageContainer>
                        </PageWidthFull>
                    }


                    <CantFindWrapper>
                        <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $margin="0 auto">
                            <CantFindWhatYouNeed />
                        </PageWidthFull>
                    </CantFindWrapper>

                    <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $margin="0 auto" >
                        <FooterContainer>
                            {productLine.faqs.length > 0 &&
                                <Faqs
                                    title={t('FAQs')}
                                    items={
                                        productLine.faqs.map((x) => ({
                                            text: x.name,
                                            link: `/articles/Knowledge/${x.link}`
                                        }))
                                    } />
                            }

                            <Topics
                                title={t('Similar products')}
                                items={
                                    productLine.similarProducts.slice(0, 3).map((x) => ({
                                        text: x.name,
                                        asset: x.mainImageUrl,
                                        heightPx: "144px",
                                        link: `/category/${categoryLink}/product/${x.link}`
                                    }))
                                }
                            />

                            <CantFindCreateCase />
                        </FooterContainer>
                    </PageWidthFull>
                </Wrapper>
            }


        </>);
};

export default ProductLinePage3;
